import { CheckCircle } from '@phosphor-icons/react';
import { Spinner } from '@/components/ui/spinner';
import { Progress } from '@/components/ui/progress';
import type { WaveStatus } from '../syncStatus.types';

import type { ExtractionDetail } from '../syncStatus.types';

interface WaveStatuses {
  w1?: string;
  w2?: string;
  w3?: string;
}

interface SyncGlobalBarProps {
  progress: number;
  isComplete: boolean;
  status: WaveStatus;
  waveStatuses?: WaveStatuses;
  pendingExtractions?: ExtractionDetail[];
  /** Fondo del track de la barra — sobre superficies tintadas el gris default
   * se ensucia; pásale `bg-white` ahí. */
  trackClassName?: string;
}

const EXTRACTION_LABELS: Record<string, string> = {
  COMPLETE_INVOICE: 'facturas completas',
  COMPLETE_RETENTION: 'retenciones completas',
  ANNUAL_TAX_RETURN: 'declaración anual',
  ANUAL_VISOR: 'visor anual',
  SAT_INBOX: 'buzón tributario',
  DECLARATIONS_RESICO: 'acuses RESICO',
  COMPLETE_PLATEC: 'acuses de plataformas',
  REAL_CSF_HISTORY: 'constancia fiscal',
  COMPLIANCE: 'opinión de cumplimiento',
  TAX_STATUS_CERTIFICATE: 'situación fiscal',
};

const resolveSubtitle = (isComplete: boolean, ws: WaveStatuses, pending: ExtractionDetail[]): string => {
  if (isComplete) return '¡Listo! Tu información está actualizada';
  if (ws.w1 !== 'completed') return 'Conectando con el SAT...';
  if (ws.w2 !== 'completed') return 'Descargando tus movimientos recientes...';
  if (pending.length > 0 && pending.length <= 2) {
    const names = pending
      .map(e => {
        const baseType = e.type?.replace(/_\d{4}$/, '') ?? '';
        return EXTRACTION_LABELS[baseType] ?? e.type ?? 'tu información';
      })
      .filter(Boolean)
      .join(' y ');
    return `Descargando ${names}, esto puede tomar unos minutos...`;
  }
  return 'Descargando tu historial completo...';
};

export const SyncGlobalBar = ({ progress, isComplete, waveStatuses = {}, pendingExtractions = [], trackClassName = 'bg-h-neutral-90' }: SyncGlobalBarProps) => {
  const title = isComplete ? 'Sincronización completa' : 'Sincronizando tu información';
  const subtitle = resolveSubtitle(isComplete, waveStatuses, pendingExtractions);

  return (
    <div className='flex flex-col gap-1.5'>
      <div className='flex items-center justify-between'>
        <span className='text-sm font-semibold text-h-text-heading leading-tight'>
          {title}
        </span>
        {isComplete ? (
          <CheckCircle weight='fill' size={20} className='text-h-success-50 shrink-0' />
        ) : (
          <div className='flex items-center gap-1.5 shrink-0'>
            <span className='text-sm font-bold text-h-primary'>{progress}%</span>
            <Spinner className='size-4 text-h-primary' />
          </div>
        )}
      </div>
      <Progress
        value={progress}
        className={`h-2 ${trackClassName} ${isComplete ? '[&>div]:bg-h-success-50' : '[&>div]:bg-h-primary'}`}
      />
      <span className='text-[11px] text-h-text-medium leading-tight'>
        {subtitle}
      </span>
    </div>
  );
};
